<template>
  <View direction="column" gap="sm">
    <ul class="items" :style="{ '--check-list-columns': columns }">
      <View
        v-for="item in shown"
        :key="item.label"
        as="li"
        align="center"
        gap="sm"
        class="item"
        :aria-label="item.value === false ? `${item.label}: not included` : undefined"
      >
        <Text v-if="item.icon" as="span" variant="body-medium" color="tertiary">
          <Icon :name="item.icon" size="small" />
        </Text>
        <Icon v-else :name="item.value ? 'check' : 'x-close'" size="small" class="mark" :class="{ off: !item.value }" />
        <!-- An explicit "no" stays in the list, struck through -->
        <Text v-if="item.value === false" as="s" variant="body-medium" color="tertiary">{{ item.label }}</Text>
        <Text v-else variant="body-medium">{{ item.label }}</Text>
      </View>
    </ul>

    <!-- Past the limit the rest folds away; the count says how many there are in all -->
    <View v-if="visible.length > limit">
      <Button variant="ghost" size="small" @click="expanded = !expanded">
        {{ expanded ? 'Show less' : `Show all ${visible.length}` }}
      </Button>
    </View>
  </View>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { Button, Icon, IconName, Text, View } from '@tallo/design-system';

export interface CheckListItem {
  label: string;
  /** True reads as a check, an explicit false as struck through; null is left out */
  value: boolean | null | undefined;
  /** The item's own picture in place of the check/cross mark */
  icon?: IconName;
}

const props = withDefaults(defineProps<{ items: CheckListItem[]; columns?: number; limit?: number }>(), {
  columns: 2,
  limit: 6,
});

const expanded = ref(false);

const visible = computed(() => props.items.filter((item) => item.value !== null && item.value !== undefined));
const shown = computed(() => (expanded.value ? visible.value : visible.value.slice(0, props.limit)));
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.items {
  display: grid;
  grid-template-columns: repeat(var(--check-list-columns), minmax(0, 1fr));
  gap: var(--t-spacing-sm) var(--t-spacing-md);
  margin: 0;
  padding: 0;
  list-style: none;

  @include mobile {
    grid-template-columns: 1fr;
  }
}

.item {
  min-width: 0;
}

.mark {
  flex-shrink: 0;
  color: var(--t-color-green-500);

  &.off {
    color: var(--t-color-neutral-500);
  }
}
</style>
